import React, { useEffect, useState, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Activity, ClipboardList, Bell, User, Heart, LogOut, Calendar, BookOpen, Sparkles, MoreHorizontal, X, Baby, UserRound } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { getAlerts } from '../api/alerts';
import { AppIcon } from './AppIcon';
import { AlertToast } from './AlertToast';
import { SuccessToast } from './SuccessToast';
import { OfflineBanner } from './OfflineBanner';
import { InstallPrompt } from './InstallPrompt';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
interface AppShellProps {
  children: React.ReactNode;
}
export const AppShell = memo(function AppShell({
  children
}: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    token,
    logout
  } = useAuth();
  const [alertsCount, setAlertsCount] = useState(0);
  const {
    isOnline
  } = useNetworkStatus();
  const [moreOpen, setMoreOpen] = useState(false);
  // Close more sheet on route change
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);
  const fetchAlertsCount = () => {
    if (token) {
      getAlerts(token).then(alerts => {
        const activeCount = alerts.filter((a: any) => a.status === 'active').length;
        setAlertsCount(activeCount);
      }).catch(err => {});
    }
  };
  useEffect(() => {
    fetchAlertsCount();
    // Set up a polling interval to keep the badge updated
    const interval = setInterval(fetchAlertsCount, 30000);
    return () => clearInterval(interval);
  }, [token, location.pathname]); // Re-fetch when navigating (e.g. after resolving)
  const handleLogout = () => {
    logout();
    navigate('/splash');
  };
  const navItems = [{
    path: '/app/dashboard',
    icon: Home,
    label: 'Home'
  }, {
    path: '/app/mother',
    icon: UserRound,
    label: 'Mother'
  }, {
    path: '/app/baby',
    icon: Baby,
    label: 'Baby'
  }, {
    path: '/app/monitor',
    icon: Activity,
    label: 'Monitor'
  }, {
    path: '/app/history',
    icon: ClipboardList,
    label: 'History'
  }, {
    path: '/app/appointments',
    icon: Calendar,
    label: 'Appts'
  }, {
    path: '/app/learn',
    icon: BookOpen,
    label: 'Learn'
  }, {
    path: '/app/aurora',
    icon: Sparkles,
    label: 'Aurora'
  }, {
    path: '/app/alerts',
    icon: Bell,
    label: 'Alerts',
    badge: alertsCount > 0
  }, {
    path: '/app/profile',
    icon: User,
    label: 'Profile'
  }];
  // Mobile: show 4 primary items + a "More" button
  const mobilePrimaryPaths = ['/app/dashboard', '/app/monitor', '/app/aurora', '/app/history'];
  const mobilePrimaryItems = mobilePrimaryPaths.map(p => navItems.find(i => i.path === p)!).filter(Boolean);
  const mobileMoreItems = navItems.filter(i => !mobilePrimaryPaths.includes(i.path));
  const moreHasBadge = mobileMoreItems.some(i => i.badge);
  const isMoreActive = mobileMoreItems.some(i => location.pathname.startsWith(i.path));
  const isActive = (path: string) => location.pathname.startsWith(path);
  const getInitials = (name: string) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };
  return <div className="min-h-screen w-full bg-background flex flex-col md:flex-row" data-id="element-136" data-mp="j7epusz99xff">
      <OfflineBanner data-id="element-137" data-mp="ec93jt1xygqt" />

      {/* MOBILE BOTTOM NAV (< 768px) */}
      <nav aria-label="Main navigation" className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-border h-[64px] pb-[env(safe-area-inset-bottom)] z-50 flex justify-around items-center px-2" data-id="element-138" data-mp="9e7hr0d4s3bn">
        {mobilePrimaryItems.map(item => {
        const active = isActive(item.path);
        const Icon = item.icon;
        return <button key={item.path} onClick={() => navigate(item.path)} aria-label={item.label} className="relative flex flex-col items-center justify-center flex-1 h-full" data-id="element-139" data-mp="rlj4q6j17lt4">
              {active && <div className="absolute top-0 w-[32px] h-[3px] bg-primary rounded-pill animate-fadeInUp" data-id="element-140" data-mp="15ceedl1cu6xr" />}
              <div className="relative mt-1" data-id="element-141" data-mp="1ac0qts9czj5i">
                <Icon size={24} className={active ? 'text-primary' : 'text-text-secondary'} data-id="element-142" data-mp="1aapnftjtsdex" />
                {item.badge && <div className="absolute -top-1 -right-1 w-2 h-2 bg-danger rounded-full" data-id="element-143" data-mp="10kd4u6vkmubf" />}
              </div>
              <span className={`text-[11px] font-medium mt-1 ${active ? 'text-primary' : 'text-text-secondary'}`} data-id="element-144" data-mp="1o95vbntfc9yn">
                {item.label}
              </span>
            </button>;
      })}
        {/* More button */}
        <button onClick={() => setMoreOpen(true)} aria-label="More" aria-expanded={moreOpen} className="relative flex flex-col items-center justify-center flex-1 h-full" data-id="element-145" data-mp="38si02etka4wb">
          {isMoreActive && <div className="absolute top-0 w-[32px] h-[3px] bg-primary rounded-pill animate-fadeInUp" data-id="element-146" data-mp="2utiddcpm002s" />}
          <div className="relative mt-1" data-id="element-147" data-mp="2puyp35njmc79">
            <MoreHorizontal size={24} className={isMoreActive ? 'text-primary' : 'text-text-secondary'} data-id="element-148" data-mp="2pw02hgs17as5" />
            {moreHasBadge && <div className="absolute -top-1 -right-1 w-2 h-2 bg-danger rounded-full" data-id="element-149" data-mp="2zrjtborqnja1" />}
          </div>
          <span className={`text-[11px] font-medium mt-1 ${isMoreActive ? 'text-primary' : 'text-text-secondary'}`} data-id="element-150" data-mp="j2ec0gfit340">
            More
          </span>
        </button>
      </nav>

      {/* MOBILE "MORE" BOTTOM SHEET */}
      <AnimatePresence data-id="element-151" data-mp="3cvyvcm3xiyie">
        {moreOpen && <div className="md:hidden fixed inset-0 z-[60]" data-id="element-152" data-mp="3cunw8zlfiexm">
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} onClick={() => setMoreOpen(false)} className="absolute inset-0 bg-black/40" data-id="element-153" data-mp="3hw3buc4u7t8m" />
            {/* Sheet */}
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 280
        }} className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[24px] pb-[calc(24px+env(safe-area-inset-bottom))] pt-3 px-4 shadow-[0_-8px_30px_rgba(107,33,232,0.15)]" role="dialog" aria-label="More navigation" data-id="element-154" data-mp="32xrhwd8umyl5">
              {/* Drag handle */}
              <div className="flex justify-center mb-2" data-id="element-155" data-mp="1oxoc5grastpy">
                <div className="w-10 h-1 bg-border rounded-full" data-id="element-156" data-mp="1jzmvt3l9b8i6" />
              </div>
              <div className="flex items-center justify-between mb-4 px-2" data-id="element-157" data-mp="1f8mz9zg1usg7">
                <h2 className="text-[16px] font-semibold text-text-primary" data-id="element-158" data-mp="1f6aap3fsdju6">
                  More
                </h2>
                <button onClick={() => setMoreOpen(false)} aria-label="Close" className="p-1.5 -mr-1.5 text-text-secondary hover:text-text-primary rounded-full" data-id="element-159" data-mp="167ykh2iu0o29">
                  <X size={20} data-id="element-160" data-mp="wak5z8ggy9cl" />
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2" data-id="element-161" data-mp="2k5p0jrro5y3i">
                {mobileMoreItems.map(item => {
              const active = isActive(item.path);
              const Icon = item.icon;
              return <button key={item.path} onClick={() => {
                setMoreOpen(false);
                navigate(item.path);
              }} aria-label={item.label} className={`relative flex flex-col items-center justify-center py-3 rounded-[14px] transition-colors ${active ? 'bg-primary/10' : 'hover:bg-border/40 active:bg-border/60'}`} data-id="element-162" data-mp="2b4qs8xa774f4">
                      <div className="relative mb-1.5" data-id="element-163" data-mp="1wfuqfhyqinmy">
                        <div className={`w-11 h-11 rounded-full flex items-center justify-center ${active ? 'bg-primary text-white' : 'bg-secondary text-primary'}`} data-id="element-164" data-mp="1we9rlg5e0ija">
                          <Icon size={22} data-id="element-165" data-mp="13tw9ye8gdcbj" />
                        </div>
                        {item.badge && <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-danger rounded-full border-2 border-white" data-id="element-166" data-mp="yvus1wmrjj1n" />}
                      </div>
                      <span className={`text-[12px] font-medium ${active ? 'text-primary' : 'text-text-primary'}`} data-id="element-167" data-mp="18tinkos70ukv">
                        {item.label}
                      </span>
                    </button>;
            })}
              </div>
              <button onClick={() => {
            setMoreOpen(false);
            handleLogout();
          }} className="w-full mt-4 flex items-center justify-center gap-2 py-3 text-[14px] font-medium text-text-secondary hover:text-danger rounded-[12px] transition-colors" data-id="element-168" data-mp="1wjczn44ouf42">
                <LogOut size={18} data-id="element-169" data-mp="21jhj930w7pyi" />
                Sign out
              </button>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* TABLET LEFT SIDEBAR (768px - 1024px) */}
      <div className="hidden md:flex lg:hidden fixed left-0 top-0 bottom-0 w-[72px] bg-white border-r border-border flex-col items-center py-6 z-50" data-id="element-170" data-mp="d1sslggnj226">
        <AppIcon size={52} className="mb-10" data-id="element-171" data-mp="35g0gn0r8w9j" />

        <div className="flex flex-col space-y-4 flex-1 w-full px-3" data-id="element-172" data-mp="36r50p20y81p">
          {navItems.map(item => {
          const active = isActive(item.path);
          const Icon = item.icon;
          return <button key={item.path} onClick={() => navigate(item.path)} aria-label={item.label} className={`relative w-full aspect-square rounded-[12px] flex items-center justify-center transition-colors ${active ? 'bg-primary/15' : 'hover:bg-border/50'}`} data-id="element-173" data-mp="vrijvtl7h5pc">
                <div className="relative" data-id="element-174" data-mp="37imkuordu0nl">
                  <Icon size={24} className={active ? 'text-primary' : 'text-text-secondary'} data-id="element-175" data-mp="37hunp9i4zuv7" />
                  {item.badge && <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-danger rounded-full border-2 border-white" data-id="element-176" data-mp="1r38cw1t3l4ft" />}
                </div>
              </button>;
        })}
        </div>

        <button onClick={handleLogout} aria-label="Sign out" className="w-full aspect-square flex items-center justify-center hover:bg-border/50 rounded-[12px] transition-colors mt-auto mx-3" data-id="element-177" data-mp="20ul7h16a68pj">
          <LogOut size={24} className="text-text-secondary" data-id="element-178" data-mp="2errmqlrsppa0" />
        </button>
      </div>

      {/* DESKTOP LEFT SIDEBAR (> 1024px) */}
      <div className="hidden lg:flex fixed left-0 top-0 bottom-0 w-[240px] bg-white border-r border-border flex-col z-50" data-id="element-179" data-mp="2jq2zb49h6dvx">
        <div className="flex items-center px-6 py-8" data-id="element-180" data-mp="kgcwm3uvhntq">
          <AppIcon size={56} className="mr-3 flex-shrink-0" data-id="element-181" data-mp="pcjurah11v14" />
          <span className="font-sans text-[20px] font-semibold text-text-primary tracking-[2px] uppercase" data-id="element-182" data-mp="1so90gh4pbolk">
            Aurora Care
          </span>
        </div>

        <div className="w-full h-[1px] bg-border mb-6" data-id="element-183" data-mp="1nqqr4pmrv7d1" />

        <div className="flex flex-col space-y-1 px-4 flex-1" data-id="element-184" data-mp="21pgnu45oj50v">
          {navItems.map(item => {
          const active = isActive(item.path);
          const Icon = item.icon;
          return <button key={item.path} onClick={() => navigate(item.path)} aria-label={item.label} className={`relative w-full h-[48px] rounded-[12px] flex items-center px-4 transition-colors ${active ? 'bg-primary/15' : 'hover:bg-primary/5'}`} data-id="element-185" data-mp="2bmbk0gehctbz">
                {active && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[24px] bg-primary rounded-r-full" data-id="element-186" data-mp="2gfxvw6ctkt6q" />}
                <div className="relative mr-3" data-id="element-187" data-mp="2pf1gxfvjrinv">
                  <Icon size={20} className={active ? 'text-primary' : 'text-text-secondary'} data-id="element-188" data-mp="2pgcl3gswmsfi" />
                  {item.badge && <div className="absolute -top-2 -right-2 w-[18px] h-[18px] bg-danger rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 border-white" data-id="element-189" data-mp="3dyw9gvhz3wx4">
                      {alertsCount > 9 ? '9+' : alertsCount}
                    </div>}
                </div>
                <span className={`text-[15px] font-medium ${active ? 'text-primary' : 'text-text-secondary'}`} data-id="element-190" data-mp="2q7qv6krolcox">
                  {item.label}
                </span>
              </button>;
        })}
        </div>

        <div className="p-4 border-t border-border" data-id="element-191" data-mp="2lbiide0yhvkx">
          <div className="flex items-center" data-id="element-192" data-mp="27axw27vu1hmo">
            <div className="w-[36px] h-[36px] bg-primary rounded-full flex items-center justify-center text-white text-[14px] font-semibold mr-3 flex-shrink-0" data-id="element-193" data-mp="27c9088t6wreb">
              {getInitials(user?.fullName)}
            </div>
            <span className="text-[14px] font-medium text-text-primary truncate flex-1 pr-2" data-id="element-194" data-mp="248l6mqwz36e2">
              {user?.fullName || 'User'}
            </span>
            <button onClick={handleLogout} aria-label="Sign out" className="p-2 hover:bg-border/50 rounded-full transition-colors flex-shrink-0" data-id="element-195" data-mp="1uirthlh6mj7p">
              <LogOut size={18} className="text-text-secondary" data-id="element-196" data-mp="1lj4zhkqs8foo" />
            </button>
          </div>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className={`flex-1 w-full pb-[80px] md:pb-0 md:ml-[72px] lg:ml-[240px] min-h-screen transition-[padding-top] duration-300 ${!isOnline ? 'pt-[44px]' : ''}`} data-id="element-197" data-mp="1gm4vdfdhtn07">
        <AlertToast data-id="element-198" data-mp="16nf9lbih7ieo" />
        <SuccessToast data-id="element-199" data-mp="16nyris84m6sp" />
        <InstallPrompt data-id="element-200" data-mp="35z8ctpvuxjzu" />
        <div className="w-full h-full max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8 animate-fadeInUp" data-id="element-201" data-mp="35xndzo2ifew6">
          {children}
        </div>
      </div>
    </div>;
});